import React, { useState, useEffect } from 'react';
import './gridPoint.css'
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import axios from 'axios';
import { ClipLoader } from "react-spinners";
import { useAbility } from '../../roles/AbilityContext';
import { getGridPointsAll, getStatus, putGridPoints } from '../../services/requests';
import { Grid, GridProps, Status } from '../../interfaces/interfaces';
import Cookies from 'js-cookie';

const GridPoint: React.FC<GridProps> = ({ id, idmonaguillo, name, active, orden, disponible, initialStatus, initialStatus2, onStatusChange, reserva, vessel, onShowModalChange, company }) => {
    const [status, setStatus] = useState(initialStatus);
    const [status2, setStatus2] = useState(initialStatus2);
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();
    const toastId = "unique-status-alertMessage";
    const [loading, setLoading] = useState(false);
    const ability = useAbility();
    const [modalVessel, setModalVessel] = useState('');
    const [isSelectOpenVessel, setIsSelectOpenVessel] = useState(false);
    const handleSelectFocusVessel = () => setIsSelectOpenVessel(true);
    const handleSelectBlurVessel = () => setIsSelectOpenVessel(false);
    const role = Cookies.get('role');
    const [isDisponible, setIsDisponible] = useState(disponible);

    /**
     * Sincroniza el estado local `status` con el valor `initialStatus` al cambiar.
     */
    useEffect(() => {
        setStatus(initialStatus);
        setLoading(false);
    }, [initialStatus]);

    /**
     * Maneja el clic en la tarjeta del estado.
     * - Si está activa (`active`) y no hay orden (`orden`), permite mostrar el modal.
     * - Si no está activa, muestra un mensaje informativo.
     */
    const handleCardClick = () => {
        if (active === true && orden === false) {
            toast.error(`No se activa ventana, "active" = true y "orden" = false.`);
            return;
        }

        if (status2 === 4) {
            toast.error(`No se activa ventana, "Status" = 4 (Sin Conexion)`);
            return;
        }

        if (status2 === 5) {
            toast.error(`No se activa ventana, "Disponible" = false`);
            return;
        }

        setShowModal(true);
        onShowModalChange?.(true);

        if (active === false) {

            toast.info(`${t('gridStatus.alertMessage')}`, {
                position: "top-right"
            });
        }
    };

    /**
     * Cambia el estado de conexión según el estado actual.
     * - Si es `'Unsincronized'`, se mantiene igual.
     * - Si es `'In use'`, cambia a `'Available'`.
     * - Si es `'Available'`, cambia a `'In use'`.
     * También actualiza el estado global llamando a `onStatusChange`.
     */
    const confirmConnection = () => {

        let vessel;

        if (!modalVessel && status2 === 1) {
            setErrorVessel(!modalVessel ? "Por favor complete este campo" : "");
            toast.error(`${t('incidents.pleaseCompleteAllFields')}.`);
            return;
        } else if (!modalVessel && status2 !== 1) {
            vessel = reserva?.vessel.Tembarcacion || ""
        } else {
            vessel = modalVessel;
        }

        const newStatus = status2 === 3 ? 3 : status2 === 2 ? 1 : 2
        setStatus2(newStatus);
        setShowModal(false);
        onShowModalChange?.(false);
        onStatusChange && onStatusChange(name, newStatus, vessel);
    };

    /**
     * Cierra el modal si se hace clic fuera del contenido (en el fondo del overlay).
     * @param e - Evento de clic del mouse sobre el overlay.
     */
    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay')) {
            setShowModal(false);
            onShowModalChange?.(false);
            setErrorVessel('');
        }
    };

    /**
     * Alterna el estado de disponibilidad de un punto en la grilla.
     * Envía la actualización al servidor, actualiza los datos locales
     * y muestra el estado correspondiente (incluyendo uno forzado si está desactivado).
     * Muestra un mensaje de éxito o error según el resultado de la operación.
     */
    const handleStateSwitch = async () => {

        setLoading(true);

        try {

            const body = {
                id: id,
                Disponible: !isDisponible,
            };

            const response = await putGridPoints(body);

            if (response.status >= 200 && response.status < 300) {
                const responseGrid = await getGridPointsAll();
                const responseStatus = await getStatus();
                const filteredStatus = (responseStatus.data.data as Status[]).filter(item => item.Tipo === "Mg");
                const filteredGrid = (responseGrid.data.data as Grid[]).filter(item => item.Tmonaguillo === name);

                if (responseGrid.status >= 200 && responseGrid.status < 300) {
                    const rawData = filteredGrid.map(grid => {
                        if (grid.Disponible === false) {
                            return {
                                ...grid,
                                Status: 5
                            };
                        }
                        return grid;
                    });
                    setIsDisponible(rawData[0].Disponible);
                    let statusMatch = filteredStatus.find(item => item.Nestado === rawData[0].Status);
                    if (rawData[0].Status === 5) {
                        statusMatch = {
                            Nestado: 5,
                            Testado: 'Desactivado',
                            Tipo: 'Mg'
                        };
                    }
                    setStatus(statusMatch ? statusMatch.Testado.toString() : "")
                    setStatus2(rawData[0].Status);
                    toast.success(`${response.data.message}`);
                    setLoading(false);
                }
            }

        } catch (error: any) {
            let message = `${t('reports.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
                setLoading(false);
            } else if (error instanceof Error) {
                message = error.message;
                setLoading(false);
            }

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
        }
    };

    /**
     * Establece automáticamente la embarcación si el usuario es cliente
     * y tiene solo una embarcación disponible.
     * 
     * Se ejecuta cuando cambian los valores de `vessel` o `role`.
     */
    useEffect(() => {
        if (vessel.length === 1 && role === "costumer") {
            setModalVessel(String(vessel[0].Tembarcacion));
        }
    }, [vessel, role]);

    const [errorVessel, setErrorVessel] = useState('');

    return (
        <>
            <div className='containerGridPoint'>
                <div className={`card card-${status2}`} onClick={handleCardClick}>
                    {loading ? (
                        <div className="loading">
                            <ClipLoader color="#007bff" size={40} />
                            <p>{t('gridStatus.updatingInformation')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="form-check form-switch">
                                <span>{idmonaguillo}</span>
                                <span>{name}</span>
                                {ability.can('update', 'AdminPage') && (
                                    <input className="form-check-input" type="checkbox" id="mySwitch" name="darkmode" checked={isDisponible} onClick={(e) => e.stopPropagation()} onChange={handleStateSwitch} title={isDisponible ? "Disponible" : "No disponible"} />
                                )}
                                {ability.cannot('update', 'AdminPage') && (
                                    <div className='emptySpace'></div>
                                )}
                            </div>
                            <span className={`icon ${active === true ? 'true' : 'false'}`} ><i className="fas fa-bolt"></i></span>
                            <span>{status}</span>
                            {status === "En Uso" && (
                                <>
                                    <div className='contenido'>
                                        <i className="fas fa-anchor"></i>
                                        <span>{reserva?.vessel.Tembarcacion}</span>
                                    </div>
                                    <div className='contenido'>
                                        <i className="fas fa-building"></i>
                                        <span>{company}</span>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {(showModal && (status2 === 2 || status2 === 1 || status2 === 3)) && (
                    <div className='modalOverlay' onClick={handleOverlayClick}>
                        <div className={`modalContent modalContent-${status2 === 3 ? status2 : ''}`}>
                            <div>
                                <div className='closeIconCompensador'></div>
                                <div>
                                    <i className={`fas fa-bolt ${(active === false && status2 === 3) ? 'disconnect' : (active === true && status2 === 3) ? 'connected' : status2 === 2 ? 'disconnect' : 'connected'}`}></i>
                                    <h6>{(active === false && status2 === 3) ? t('gridStatus.disconnectFromGridPoint') : (active === true && status2 === 3) ? t('gridStatus.connectToGridPoint') : status2 === 2 ? t('gridStatus.disconnectFromGridPoint') : t('gridStatus.connectToGridPoint')}</h6>
                                </div>
                                <span className="closeIcon" onClick={() => {
                                    onShowModalChange?.(false);
                                    setShowModal(false)
                                }}>&times;</span>
                            </div>
                            <h6>{name}</h6>
                            <p>
                                {t('gridStatus.thisGridPointIsCurrently')} {(active === false && status2 === 3) ? t('gridStatus.connected2') + ". " : (active === true && status2 === 3) ? t('gridStatus.availableForConnection') + '. ' : status2 === 2 ? t('gridStatus.connected2') + ". " : t('gridStatus.availableForConnection') + '. '}
                                {/* {t('gridStatus.doYouWantTo')} {(active === false && status2 === 3) ? t('gridStatus.disconnect') : (active === true && status2 === 3) ? t('gridStatus.connect') : status2 === 2 ? t('gridStatus.disconnect') : t('gridStatus.connect')} {t('gridStatus.to')} <strong>{reserva?.vessel.Tembarcacion}</strong> {reserva?.vessel.Tembarcacion === 'Generic' ? '' : ` ${t('gridStatus.withEstimatedDate')} ${formatDate(reserva?.Festimada || "")}`}? */}
                                {t('gridStatus.doYouWantTo')} {(active === false && status2 === 3) ? t('gridStatus.disconnect') : (active === true && status2 === 3) ? t('gridStatus.connect') : status2 === 2 ? t('gridStatus.disconnect') : t('gridStatus.connect')}?
                            </p>
                            {status2 === 1 && (
                                <div className="select-wrapper">
                                    <div className="input-row">
                                        <h6><span>*</span> {t('gridStatus.selectAVessel')}</h6>
                                        <select
                                            className={`form-control ${errorVessel ? 'is-invalid' : ''}`}
                                            value={modalVessel}
                                            onChange={(e) => {
                                                setModalVessel(e.target.value);
                                                setErrorVessel('');
                                            }}
                                            onFocus={handleSelectFocusVessel}
                                            onBlur={handleSelectBlurVessel}
                                            disabled={role === "costumer" && vessel.length === 1}
                                        >
                                            {role !== "costumer" && (
                                                <option value="">{`${t('registers.selectA1')} ${t('registers.vessel').toLowerCase()}`} ...</option>
                                            )}

                                            {vessel.map((item) => (
                                                <option key={item.Nembarcacion} value={String(item.Tembarcacion)}>
                                                    {item.Tembarcacion}
                                                </option>
                                            ))}
                                        </select>
                                        <span className={`icon ${errorVessel ? 'error' : ''}`}>
                                            {isSelectOpenVessel ? (
                                                <i className="fas fa-chevron-up"></i>
                                            ) : (
                                                <i className="fas fa-chevron-down"></i>
                                            )}
                                        </span>
                                    </div>
                                    {errorVessel && (
                                        <div className="invalid-feedback d-block">{errorVessel}</div>
                                    )}
                                </div>
                            )}
                            <button className={`icon icon-${status2 === 3 ? status2 : ''}`} onClick={confirmConnection}>
                                <i className={`fas fa-power-off ${(active === false && status2 === 3) ? 'disconnect' : (active === true && status2 === 3) ? 'connected' : status2 === 2 ? 'disconnect' : 'connected'} ${status2 === 3 ? 'unsincronized' : ''} `}></i>
                            </button>
                            <p className="alert-warning">
                                <i className="fas fa-exclamation-triangle"></i>
                                &nbsp; {t('gridStatus.alertMessage')}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default GridPoint;
